import { CheckIcon, ChevronRightIcon, CircleIcon } from "lucide-react";
import type React from "react";
import {
  Collection,
  composeRenderProps,
  type PopoverProps,
  Header as RACHeader,
  Keyboard as RACKeyboard,
  Menu as RACMenu,
  MenuItem as RACMenuItem,
  type MenuItemProps as RACMenuItemProps,
  type MenuProps as RACMenuProps,
  MenuSection as RACMenuSection,
  type MenuSectionProps as RACMenuSectionProps,
  MenuTrigger as RACMenuTrigger,
  Separator as RACSeparator,
  type SeparatorProps as RACSeparatorProps,
  SubmenuTrigger as RACSubmenuTrigger,
} from "react-aria-components";

import { cn } from "@/components/ui/utils";
import { Popover } from "./popover";
import { SelectSection } from "./select";

export const MenuTrigger = RACMenuTrigger;

export const MenuSubTrigger = RACSubmenuTrigger;

export function Menu<T extends object>({
  className,
  placement,
  isOpen,
  onOpenChange,
  ...props
}: RACMenuProps<T> &
  Pick<PopoverProps, "placement" | "isOpen" | "onOpenChange">) {
  return (
    <Popover
      className="w-auto p-0"
      placement={placement}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      aria-label={props["aria-label"]}
    >
      <RACMenu
        className={cn(
          "max-h-[inherit] overflow-auto rounded-md p-1 outline-0 [clip-path:inset(0_0_0_0_round_calc(var(--radius)-2px))]",
          className,
        )}
        {...props}
      />
    </Popover>
  );
}

export function MenuItem({
  children,
  className,
  ...props
}: RACMenuItemProps & { isDanger?: boolean }) {
  return (
    <RACMenuItem
      textValue={
        props.textValue || (typeof children === "string" ? children : undefined)
      }
      className={composeRenderProps(className, (className) =>
        cn(
          "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden",
          /* Disabled */
          "data-disabled:pointer-events-none data-disabled:opacity-50",
          /* Focused */
          "data-focused:bg-accent data-focused:text-accent-foreground ",
          /* Selection Mode */
          "data-selection-mode:pl-9",
          /* Danger */
          props.isDanger && "text-destructive",
          "[&_svg:not([class*=size])]:size-4",
          className,
        ),
      )}
      {...props}
    >
      {composeRenderProps(children, (children, renderProps) => (
        <>
          <span className="absolute left-2 flex size-6 items-center justify-center">
            {renderProps.isSelected && (
              <>
                {renderProps.selectionMode === "single" && (
                  <CircleIcon className="size-2 fill-current" />
                )}
                {renderProps.selectionMode === "multiple" && (
                  <CheckIcon className="size-4" />
                )}
              </>
            )}
          </span>

          {children}

          {renderProps.hasSubmenu && (
            <ChevronRightIcon className="ml-auto size-4" />
          )}
        </>
      ))}
    </RACMenuItem>
  );
}

type MenuHeaderProps = React.ComponentProps<typeof RACHeader> & {
  inset?: boolean;
  separator?: boolean;
};

export function MenuHeader({
  className,
  inset,
  separator = true,
  ...props
}: MenuHeaderProps) {
  return (
    <RACHeader
      className={cn(
        "text-muted-foreground px-2 py-1.5 text-xs font-medium",
        inset && "pl-8",
        separator && "-mx-1 mb-1 border-b pb-2.5",
        className,
      )}
      {...props}
    />
  );
}

export function MenuSeparator({ className, ...props }: RACSeparatorProps) {
  return (
    <RACSeparator
      className={cn("-mx-1 my-1 h-px bg-muted", className)}
      {...props}
    />
  );
}

export function MenuKeyboard({
  className,
  ...props
}: React.ComponentProps<typeof RACKeyboard>) {
  return (
    <RACKeyboard
      className={cn(
        "ml-auto text-xs tracking-widest text-muted-foreground/70 pl-4",
        className,
      )}
      {...props}
    />
  );
}

export interface MenuSectionProps<T> extends RACMenuSectionProps<T> {
  title?: string;
}

export function MenuSection<T extends object>({
  title,
  children,
  ...props
}: MenuSectionProps<T>) {
  return (
    <RACMenuSection
      // className="first:-mt-[1px] after:content-[''] after:block after:h-[5px]"
      {...props}
    >
      {title && <MenuHeader separator={false}>{title}</MenuHeader>}
      <Collection items={props.items}>{children}</Collection>
    </RACMenuSection>
  );
}
